import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';

const STORAGE_BUCKET = 'product-details';
const MAX_FILE_SIZE = 512000; // 500KB in bytes

/**
 * Get all product details for a feature group
 */
export const getProductDetails = async (featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('product_details')
      .select('*')
      .eq('feature_group_id', featureGroupId)
      .order('document_type', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching product details:', error);
    throw error;
  }
};

/**
 * Get product detail by ID
 */
export const getProductDetailById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('product_details')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching product detail:', error);
    throw error;
  }
};

/**
 * Get product detail by type for a feature group
 */
export const getProductDetailByType = async (featureGroupId, documentType) => {
  try {
    const { data, error } = await supabase
      .from('product_details')
      .select('*')
      .eq('feature_group_id', featureGroupId)
      .eq('document_type', documentType)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows found
    return data || null;
  } catch (error) {
    logger.error('Error fetching product detail by type:', error);
    throw error;
  }
};

/**
 * Upload or update a product detail document
 * @param {string} featureGroupId - Feature group ID
 * @param {string} documentType - One of: 'Product Overview', 'Technical Specs', 'User Guide', 'API Documentation', 'Glossary'
 * @param {File|Blob|string} fileContent - File object, Blob, or markdown string
 * @param {string} fileName - Original filename for display
 * @returns {Promise<Object>} Product detail record
 */
export const uploadProductDetail = async (featureGroupId, documentType, fileContent, fileName) => {
  try {
    // Validate document type
    const validTypes = ['Product Overview', 'Technical Specs', 'User Guide', 'API Documentation', 'Glossary'];
    if (!validTypes.includes(documentType)) {
      throw new Error(`Invalid document type. Must be one of: ${validTypes.join(', ')}`);
    }

    // Convert fileContent to Blob if needed
    let blob;
    let fileSize;
    
    if (fileContent instanceof File) {
      blob = fileContent;
      fileSize = fileContent.size;
    } else if (fileContent instanceof Blob) {
      blob = fileContent;
      fileSize = fileContent.size;
    } else if (typeof fileContent === 'string') {
      // Convert string to Blob
      blob = new Blob([fileContent], { type: 'text/markdown' });
      fileSize = new TextEncoder().encode(fileContent).length;
    } else {
      throw new Error('Invalid file content. Must be File, Blob, or string.');
    }

    // Validate file size
    if (fileSize > MAX_FILE_SIZE) {
      throw new Error(`File size (${fileSize} bytes) exceeds maximum allowed size of ${MAX_FILE_SIZE} bytes (500KB)`);
    }

    // Validate non-empty content
    if (fileSize === 0) {
      throw new Error('File content cannot be empty');
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Storage path: {feature_group_id}/{document_type}.md
    const storagePath = `${featureGroupId}/${documentType}.md`;

    // Check if document of this type already exists
    const existing = await getProductDetailByType(featureGroupId, documentType);

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(storagePath, blob, {
        contentType: 'text/markdown',
        upsert: true, // Replace if exists
        cacheControl: '3600',
      });

    if (uploadError) {
      logger.error('Error uploading to storage:', uploadError);
      throw new Error(`Failed to upload file: ${uploadError.message}`);
    }

    // Prepare database record
    const recordData = {
      feature_group_id: featureGroupId,
      document_type: documentType,
      storage_path: storagePath,
      file_name: fileName || `${documentType}.md`,
      file_size: fileSize,
      updated_by: user.id,
    };

    let result;
    if (existing) {
      // Update existing record
      recordData.updated_at = new Date().toISOString();
      const { data, error } = await supabase
        .from('product_details')
        .update(recordData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        // If update fails, try to delete the uploaded file
        await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
        throw error;
      }
      result = data;
    } else {
      // Create new record
      recordData.created_by = user.id;
      const { data, error } = await supabase
        .from('product_details')
        .insert([recordData])
        .select()
        .single();

      if (error) {
        // If insert fails, try to delete the uploaded file
        await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
        throw error;
      }
      result = data;
    }

    return result;
  } catch (error) {
    logger.error('Error uploading product detail:', error);
    throw error;
  }
};

/**
 * Delete a product detail document
 * @param {string} id - Product detail ID
 */
export const deleteProductDetail = async (id) => {
  try {
    // Get the record to find storage path
    const productDetail = await getProductDetailById(id);
    if (!productDetail) {
      throw new Error('Product detail not found');
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([productDetail.storage_path]);

    if (storageError) {
      logger.warn('Error deleting from storage (continuing with DB delete):', storageError);
      // Continue with DB delete even if storage delete fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('product_details')
      .delete()
      .eq('id', id);

    if (dbError) throw dbError;
  } catch (error) {
    logger.error('Error deleting product detail:', error);
    throw error;
  }
};

/**
 * Download product detail content
 * @param {string} id - Product detail ID
 * @returns {Promise<{content: string, fileName: string}>}
 */
export const downloadProductDetail = async (id) => {
  try {
    const productDetail = await getProductDetailById(id);
    if (!productDetail) {
      throw new Error('Product detail not found');
    }

    // Download from storage
    const { data: fileData, error: downloadError } = await supabase.storage
      .from(STORAGE_BUCKET)
      .download(productDetail.storage_path);

    if (downloadError) throw downloadError;

    // Convert blob to text
    const content = await fileData.text();

    return {
      content,
      fileName: productDetail.file_name || `${productDetail.document_type}.md`,
    };
  } catch (error) {
    logger.error('Error downloading product detail:', error);
    throw error;
  }
};

/**
 * Get product details content for AI context
 * Fetches all product details for a feature group and returns formatted markdown
 * @param {string} featureGroupId - Feature group ID
 * @returns {Promise<string>} Formatted markdown string with all product details
 */
export const getProductDetailsForAIContext = async (featureGroupId) => {
  try {
    const productDetails = await getProductDetails(featureGroupId);
    
    if (!productDetails || productDetails.length === 0) {
      return '';
    }

    // Fetch content from storage for each document
    const contentPromises = productDetails.map(async (doc) => {
      try {
        const { data: fileData, error } = await supabase.storage
          .from(STORAGE_BUCKET)
          .download(doc.storage_path);

        if (error || !fileData) {
          logger.warn(`Error loading product detail ${doc.document_type}:`, error);
          return null;
        }

        const content = await fileData.text();
        return `## ${doc.document_type}\n\n${content}`;
      } catch (err) {
        logger.warn(`Error processing product detail ${doc.document_type}:`, err);
        return null;
      }
    });

    const contents = await Promise.all(contentPromises);
    const validContents = contents.filter(content => content !== null);

    if (validContents.length === 0) {
      return '';
    }

    return validContents.join('\n\n---\n\n');
  } catch (error) {
    logger.error('Error getting product details for AI context:', error);
    return '';
  }
};
